import Text, { TEXT_VARIANT_STYLES } from "shared/components/data-display/text/Text";
import styled, { createGlobalStyle, css } from "styled-components";

import { COLOR_VALUES } from "shared/config/colors";
import DropdownSingleSelectWithoutSearch from "shared/components/inputs/dropdown/DropdownSingleSelectWithoutSearch";
import { DropdownStylesError } from "shared/components/inputs/dropdown/Dropdown.styles";
import { FEEDBACK_TYPES } from "shared/config/feedbackTypes";
import Icon from "shared/components/utils/Icon";
import PropTypes from "prop-types";
import React from "react";

export const DropdownListStyled = createGlobalStyle`
    .dropdownListContainer{
        width:132.29px;
        top: calc(100%);
        ul > div{
            min-height: 36px;
        }
        li{
            margin-left: 16px;
            margin-right: 8px;
            padding: 8px 6px;
            border-bottom: 1px solid ${COLOR_VALUES.Gray_6};
        }
    }
`;

export const StyledDropdown = styled(DropdownSingleSelectWithoutSearch)`
    width: 100%;
    height: 52px;
    padding: 10px 12px;
    border-left: none;
    border-right: none;
    border-top: none;
    border-radius: 0;
    border-color: transparent;
    color: ${({ $hasSelection }) =>
        $hasSelection ? COLOR_VALUES.Gray_1 : COLOR_VALUES.Gray_4};

    div {
        ${TEXT_VARIANT_STYLES.Body_2_2}
    }

    &:visited {
        div,
        svg {
            color: ${COLOR_VALUES.Gray_1};
        }
    }

    &:hover {
        div,
        svg {
            color: ${COLOR_VALUES.Black};
        }
    }

    ${(props) =>
        props.feedbackType === FEEDBACK_TYPES.Error &&
        css`
            ${DropdownStylesError}
        `}

    ${(props) =>
        props.disabled &&
        css`
            background: transparent;
            border-color: transparent;
        `}
`;

const MobileOverlay = styled.button`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 10000;
    border: none;
    padding: 0;
`;

const MobileModal = styled.dialog`
    background-color: #fff;
    border-radius: 8px;
    padding: 24px;
    max-width: 280px;
    width: calc(100% - 32px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10001;
    border: none;
    margin: 0;
`;

const MobileHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e8ec;
`;

const MobileCloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
        background-color: #edeff2;
    }
`;

const MobileOption = styled.button`
    width: 100%;
    padding: 16px;
    margin-bottom: 8px;
    background-color: #fff;
    border: 1px solid #e5e8ec;
    border-radius: 4px;
    cursor: pointer;
    text-align: left;
    font-size: 1rem;
    color: #3f434d;
    transition: all 0.2s ease;

    &:hover {
        background-color: #edeff2;
        border-color: #dadbde;
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const MobileTimeOffTriggerButton = styled.button`
    width: 100%;
    height: 100%;
    padding: 16px 12px;
    background-color: #fff;
    border: none;
    border-radius: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.875rem;
    text-align: left;
    color: ${({ $hasSelection }) => ($hasSelection ? "#3f434d" : "#a3a6ab")};

    &:hover {
        background-color: #edeff2;
    }

    &:disabled {
        cursor: not-allowed;
        background-color: #f5f6f8;
        color: #5f6270;
    }

    & span {
        flex: 1;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
`;

// Mobile Trigger Button for Time Off Cells (Small Breakpoint Only)
export const MobileTimeOffTrigger = ({ children, onClick, disabled, hasSelection }) => (
    <MobileTimeOffTriggerButton
        $hasSelection={hasSelection}
        onClick={onClick}
        disabled={disabled}
    >
        {children}
    </MobileTimeOffTriggerButton>
);

MobileTimeOffTrigger.propTypes = {
    children: PropTypes.node,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    hasSelection: PropTypes.bool,
};

// Mobile Modal Component
export const MobileTimeOffDropdown = ({
    isOpen,
    onClose,
    options,
    value,
    onChange,
    disabled
}) => {
    if (!isOpen || disabled) return null;

    return (
        <>
            <MobileOverlay
                aria-label="Close"
                onClick={onClose}
                onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
            />
            <MobileModal open>
                <MobileHeader>
                    <Text variant="Headline_4" color="Black">
                        Select Hours
                    </Text>
                    <MobileCloseButton onClick={onClose}>
                        <Icon name="close" color="Gray_2" size={20} />
                    </MobileCloseButton>
                </MobileHeader>

                {options.map((option) => (
                    <MobileOption
                        key={option.value}
                        onClick={() => {
                            onChange(option.value);
                            onClose();
                        }}
                    >
                        {option.label}
                    </MobileOption>
                ))}
            </MobileModal>
        </>
    );
};

MobileTimeOffDropdown.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    options: PropTypes.array,
    value: PropTypes.any,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
};
